import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, ScrollView } from 'react-native';
import { FormSection } from '@/components';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { roleNamesAndDescriptions } from '@/types/admin/userManagement';
import { IUserState, IOrganization, IProperty } from '@/types/admin/navigation';
import { MultiSelectPicker } from '@/components';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { useAnchoredPosition } from '@/hooks/common/useAnchoredPosition';
import { useResponsive } from '@/hooks/common/useResponsive';
import { SVG_COMPONENTS } from '@/assets/admin';

interface UserRoleSelectionSectionProps {
  formData: {
    roleName: string;
    organizations?: string[];
    properties?: string[];
  };
  errors: Record<string, string>;
  onFieldChange: (field: string, value: any) => void;
  adminUser: IUserState;
}

export const UserRoleSelectionSection: React.FC<UserRoleSelectionSectionProps> = ({
  formData,
  errors,
  onFieldChange,
  adminUser,
}) => {
  const styles = useStyles();
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const availableRoles = useMemo(() => {
    const userRoles = adminUser.roles?.map((role) => role.name) || [];
    const roles: any[] = [];

    if (userRoles.includes(roleNamesAndDescriptions.superAdmin.value)) {
      roles.push(
        roleNamesAndDescriptions.superAdmin,
        roleNamesAndDescriptions.orgAdmin,
        roleNamesAndDescriptions.billingAdmin,
        roleNamesAndDescriptions.propertyAdmin,
      );
    } else if (userRoles.includes(roleNamesAndDescriptions.orgAdmin.value)) {
      roles.push(
        roleNamesAndDescriptions.orgAdmin,
        roleNamesAndDescriptions.billingAdmin,
        roleNamesAndDescriptions.propertyAdmin,
      );
    } else if (userRoles.includes(roleNamesAndDescriptions.billingAdmin.value)) {
      roles.push(
        roleNamesAndDescriptions.billingAdmin,
        roleNamesAndDescriptions.propertyAdmin,
      );
    } else if (userRoles.includes(roleNamesAndDescriptions.propertyAdmin.value)) {
      roles.push(roleNamesAndDescriptions.propertyAdmin);
    }

    return roles;
  }, [adminUser.roles]);

  const organizationOptions = useMemo(() => {
    return (adminUser.organizations || []).map((org: IOrganization) => ({
      label: org.name,
      value: org.name,
    }));
  }, [adminUser.organizations]);

  const propertyOptions = useMemo(() => {
    return (adminUser.properties || []).map((prop: IProperty) => ({
      label: prop.marketingName,
      value: prop.marketingName,
    }));
  }, [adminUser.properties]);

  const showOrganizationSelector = formData.roleName === roleNamesAndDescriptions.orgAdmin.value;
  const showPropertySelector = [
    roleNamesAndDescriptions.propertyAdmin.value,
    roleNamesAndDescriptions.billingAdmin.value,
  ].includes(formData.roleName);

  return (
    <>
      <View style={styles.stepIndicator}>
        <Text style={styles.stepText}>Step 2 of 3: Assign User Permissions</Text>
      </View>

      <FormSection
        title="User Permissions"
        subtitle="Select the role and access permissions for this user"
      >
        <View style={styles.container}>
          {/* Role Selection */}
          <View style={styles.roleSection}>
            <Text style={styles.sectionTitle}>User Role</Text>
            {errors.roleName && <Text style={styles.errorText}>{errors.roleName}</Text>}

            <View style={styles.roleOptions}>
              {availableRoles.map((role: any) => (
                <RoleOption
                  key={role.value}
                  role={role}
                  isSelected={formData.roleName === role.value}
                  onSelect={() => onFieldChange('roleName', role.value)}
                />
              ))}
            </View>
          </View>

          {/* Organization Selector */}
          {showOrganizationSelector && (
            <View style={styles.selectorSection}>
              {isDesktop ? (
                <DesktopMultiSelect
                  label="Related Organizations"
                  options={organizationOptions}
                  selectedValues={formData.organizations || []}
                  onSelectionChange={(values: string[]) => onFieldChange('organizations', values)}
                  placeholder="Select organizations"
                  error={errors.organizations}
                  required
                />
              ) : (
                <MultiSelectPicker
                  label="Related Organizations"
                  options={organizationOptions}
                  selectedValues={formData.organizations || []}
                  onSelectionChange={(values: string[]) => onFieldChange('organizations', values)}
                  placeholder="Select organizations"
                  error={errors.organizations}
                  required
                />
              )}
            </View>
          )}

          {/* Property Selector */}
          {showPropertySelector && (
            <View style={styles.selectorSection}>
              {isDesktop ? (
                <DesktopMultiSelect
                  label="Related Properties"
                  options={propertyOptions}
                  selectedValues={formData.properties || []}
                  onSelectionChange={(values: string[]) => onFieldChange('properties', values)}
                  placeholder="Select properties"
                  error={errors.properties}
                  required
                />
              ) : (
                <MultiSelectPicker
                  label="Related Properties"
                  options={propertyOptions}
                  selectedValues={formData.properties || []}
                  onSelectionChange={(values: string[]) => onFieldChange('properties', values)}
                  placeholder="Select properties"
                  error={errors.properties}
                  required
                />
              )}
            </View>
          )}
        </View>
      </FormSection>
    </>
  );
};

interface RoleOptionProps {
  role: {
    name: string;
    value: string;
    description: string;
  };
  isSelected: boolean;
  onSelect: () => void;
}

const RoleOption: React.FC<RoleOptionProps> = ({ role, isSelected, onSelect }) => {
  const styles = useStyles();
  return (
    <TouchableOpacity
      style={[styles.roleOption, isSelected && styles.roleOptionSelected]}
      onPress={onSelect}
      activeOpacity={0.7}
    >
      <View style={styles.roleOptionContent}>
        <View style={styles.roleOptionHeader}>
          <Text style={[styles.roleOptionTitle, isSelected && styles.roleOptionTitleSelected]}>
            {role.name}
          </Text>
          <View style={[styles.radioButton, isSelected && styles.radioButtonSelected]}>
            {isSelected && <View style={styles.radioButtonInner} />}
          </View>
        </View>
        <Text style={[styles.roleOptionDescription, isSelected && styles.roleOptionDescriptionSelected]}>
          {role.description}
        </Text>
      </View>
    </TouchableOpacity>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors, semantic }) => {
    const accent = PRIMITIVE_COLORS.resioBlue;
    return StyleSheet.create({
      stepIndicator: {
        backgroundColor: 'transparent',
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: components.admin.dataTable.border,
        marginBottom: 16,
      },
      stepText: {
        fontSize: 14,
        fontWeight: '500',
        color: components.admin.dataCell.textMuted,
        textAlign: 'center',
      },
      container: {
        gap: 24,
      },
      roleSection: {
        gap: 12,
      },
      sectionTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: colors.text.primary,
      },
      errorText: {
        fontSize: 12,
        color: semantic.status.error,
        marginTop: -8,
      },
      roleOptions: {
        gap: 12,
      },
      roleOption: {
        backgroundColor: components.admin.formInput.bg,
        borderRadius: 12,
        padding: 16,
        borderWidth: 2,
        borderColor: components.admin.formInput.border,
      },
      roleOptionSelected: {
        borderColor: accent,
        backgroundColor: accent + '10',
      },
      roleOptionContent: {
        gap: 8,
      },
      roleOptionHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
      },
      roleOptionTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: colors.text.primary,
        flex: 1,
      },
      roleOptionTitleSelected: {
        color: accent,
      },
      roleOptionDescription: {
        fontSize: 14,
        color: components.admin.dataCell.textMuted,
        lineHeight: 20,
      },
      roleOptionDescriptionSelected: {
        color: colors.text.primary,
      },
      radioButton: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 2,
        borderColor: components.admin.formInput.border,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: components.admin.formInput.bg,
      },
      radioButtonSelected: {
        borderColor: accent,
      },
      radioButtonInner: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: accent,
      },
      selectorSection: {
        gap: 8,
      },
    });
  });

// Desktop multi-select with dropdown panel (renders in a Modal to escape clipping)
const DesktopMultiSelect = ({
  label,
  options,
  selectedValues,
  onSelectionChange,
  placeholder,
  error,
  required,
}: {
  label: string;
  options: Array<{ label: string; value: string }>;
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  placeholder?: string;
  error?: string;
  required?: boolean;
}) => {
  const styles = useDesktopMultiSelectStyles();
  const { admin, redesign, semantic } = useAdminColors();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const triggerRef = React.useRef<View>(null);
  useAnchoredPosition(anchor, { width: Math.min(360, anchor?.w || 280), maxHeight: 320 });

  const toggleValue = (value: string) => {
    if (selectedValues.includes(value)) {
      onSelectionChange(selectedValues.filter((v) => v !== value));
    } else {
      onSelectionChange([...selectedValues, value]);
    }
  };

  const displayText = selectedValues.length > 0
    ? `${selectedValues.length} selected`
    : (placeholder || 'Select');

  return (
    <View style={{ gap: 6 }}>
      <Text style={styles.label}>
        {label}{required ? ' *' : ''}
      </Text>
      {error ? (
        <Text style={[styles.errorText, { color: semantic.status.error }]}>{error}</Text>
      ) : null}
      <View style={{ position: 'relative' }} ref={triggerRef}>
        <TouchableOpacity
          onPress={() => {
            const node: any = triggerRef.current;
            if (node && node.measureInWindow) {
              node.measureInWindow((x: number, y: number, w: number, h: number) => {
                setAnchor({ x, y, w, h });
                setOpen(true);
              });
            } else {
              setOpen(true);
            }
          }}
          activeOpacity={0.7}
          style={styles.selector}
        >
          <Text style={styles.selectorText}>{displayText}</Text>
          {open ? (
            <SVG_COMPONENTS.CHEVRON_UP width={14} height={14} color={admin.dataCell.textMuted} />
          ) : (
            <SVG_COMPONENTS.CHEVRON_DOWN width={14} height={14} color={admin.dataCell.textMuted} />
          )}
        </TouchableOpacity>
      </View>

      {open && anchor && (
        <Modal transparent visible onRequestClose={() => setOpen(false)}>
          <TouchableOpacity activeOpacity={1} onPress={() => setOpen(false)} style={{ flex: 1 }}>
            <View style={{ flex: 1 }}>
              <AnchoredDropdown
                isOpen={open}
                anchor={anchor}
                width={Math.min(anchor.w, 360)}
                maxHeight={320}
                onRequestClose={() => setOpen(false)}
              >
                <ScrollView style={{ maxHeight: 320 }}>
                  {options.map((opt) => {
                    const isSelected = selectedValues.includes(opt.value);
                    return (
                      <TouchableOpacity
                        key={`${opt.label}-${opt.value}`}
                        style={styles.optionRow}
                        onPress={() => toggleValue(opt.value)}
                        activeOpacity={0.7}
                      >
                        <Text style={styles.optionText}>{opt.label}</Text>
                        {isSelected && (
                          <SVG_COMPONENTS.CHECK_CIRCLE width={16} height={16} color={PRIMITIVE_COLORS.resioBlue} />
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
                <View style={styles.actionsRow}>
                  <TouchableOpacity
                    onPress={() => onSelectionChange([])}
                    style={styles.clearButton}
                  >
                    <Text style={styles.clearButtonText}>Clear all</Text>
                  </TouchableOpacity>
                </View>
              </AnchoredDropdown>
            </View>
          </TouchableOpacity>
        </Modal>
      )}
    </View>
  );
};

const useDesktopMultiSelectStyles = () =>
  useThemedStyles(({ components, colors }) =>
    StyleSheet.create({
      label: {
        fontSize: 14,
        fontWeight: '600',
        color: colors.text.primary,
      },
      errorText: {
        fontSize: 12,
        marginTop: -4,
      },
      selector: {
        borderWidth: 1,
        borderColor: components.admin.formInput.border,
        backgroundColor: components.admin.formInput.bg,
        borderRadius: 6,
        paddingHorizontal: 12,
        paddingVertical: 10,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
      },
      selectorText: {
        fontSize: 14,
        color: colors.text.primary,
      },
      optionRow: {
        paddingHorizontal: 12,
        paddingVertical: 10,
        borderBottomWidth: 1,
        borderBottomColor: components.admin.dataTable.border,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: components.admin.selector.dropdownBg,
      },
      optionText: {
        fontSize: 14,
        color: colors.text.primary,
      },
      actionsRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 8,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderTopWidth: 1,
        borderTopColor: components.admin.dataTable.border,
        backgroundColor: components.admin.selector.dropdownBg,
      },
      clearButton: {
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: components.admin.formInput.border,
        backgroundColor: components.admin.formInput.bg,
      },
      clearButtonText: {
        fontSize: 13,
        color: components.admin.dataCell.textMuted,
      },
    }),
  );
